import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authService } from '../services/authService';
import { Trans, useLingui } from '@lingui/react/macro';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  // Verify-before-create: after the signup form, a 6-digit emailed code must
  // be proven before the account is created.
  const [codeStep, setCodeStep] = useState(false);
  const [signupCode, setSignupCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { login, ssoLogin, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useLingui();
  const { isDark } = useTheme();

  const PASSWORD_HINT = t`At least 8 characters, including an uppercase letter, a lowercase letter, and a number.`;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Check for OAuth error or session expiration in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const sessionExpired = urlParams.get('session_expired');
    
    if (sessionExpired === 'true') {
      setError(t`Your session has expired. Please sign in again to continue.`);
      window.history.replaceState({}, '', '/login');
    } else if (errorParam) {
      setError(errorParam === 'authentication_failed' ? t`Authentication failed. Please try again.` :
               errorParam === 'callback_error' ? t`OAuth callback error. Please try again.` :
               errorParam === 'invalid_callback_data' ? t`Invalid authentication data received.` :
               t`An error occurred during authentication.`);
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(null);
    setResendMessage('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(email, password);
        if (result.success) {
          const { onboardingService } = await import('../services/onboardingService');
          const done = await onboardingService.getStatus();
          navigate(done ? '/dashboard' : '/onboarding');
        } else if (result.emailNotVerified) {
          setNeedsVerification(result.email || email);
          setError(result.error || t`Please verify your email before signing in.`);
        } else {
          setError(result.error || t`Login failed`);
        }
      } else {
        // Verify-before-create: send the code first; the account is only
        // created after the code checks out.
        const result = await authService.requestSignupCode(email);
        if (result.success) {
          setSignupCode('');
          setCodeError('');
          setResendCooldown(60);
          setCodeStep(true);
        } else {
          setError(result.error || t`Could not send a verification code`);
        }
      }
    } catch (err: any) {
      setError(err.message || t`An error occurred`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(signupCode.trim())) {
      setCodeError(t`Enter the 6-digit code from the email.`);
      return;
    }
    setVerifyBusy(true);
    setCodeError('');
    try {
      const verify = await authService.verifySignupCode(email, signupCode.trim());
      if (!verify.success || !verify.signupToken) {
        setCodeError(verify.error || t`That code is invalid or has expired.`);
        return;
      }
      const result = await authService.register(firstName, lastName, email, password, verify.signupToken);
      if (result.success) {
        await refreshUser();
        navigate('/onboarding');
      } else {
        setCodeError(result.error || t`Registration failed`);
      }
    } catch (err: any) {
      setCodeError(err.message || t`Verification failed`);
    } finally {
      setVerifyBusy(false);
    }
  };

  const handleResendCode = async () => {
    setResendBusy(true);
    setResendMessage('');
    const r = await authService.requestSignupCode(email);
    setResendBusy(false);
    setResendMessage(r.message || t`If that address can receive mail, we've sent a new code.`);
    if (r.success) setResendCooldown(60);
  };

  const handleResend = async (target: string) => {
    setResendBusy(true);
    setResendMessage('');
    const r = await authService.resendVerification(target);
    setResendBusy(false);
    setResendMessage(r.message || t`If that account exists, a new verification email has been sent.`);
  };

  const handleSSO = async (provider: 'google' | 'microsoft') => {
    setError('');
    setLoading(true);
    try {
      await ssoLogin(provider);
    } catch (err: any) {
      setError(err.message || t`SSO login failed`);
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-8" style={{ fontFamily: "'Inter', sans-serif", backgroundColor: 'var(--color-bg)', color: 'var(--color-text)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="max-w-md w-full">
        <div className="glass-panel rounded-3xl p-6 sm:p-8 border border-blue-100 dark:border-blue-900/30">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src={isDark ? "/privacypal-logo-light.png" : "/privacypal-logo.png"} 
                alt="PrivacyPal" 
                className="w-48 sm:w-64 h-auto prism-edge" 
              />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {isLogin ? <Trans>Welcome Back</Trans> : <Trans>Create Account</Trans>}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {isLogin ? (
                <Trans>Sign in to your PrivacyPal account</Trans>
              ) : (
                <Trans>Start protecting your data with PrivacyPal</Trans>
              )}
            </p>
          </div>

          {codeStep ? (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg text-blue-800 dark:text-blue-300 text-sm">
                <p className="font-semibold mb-1"><Trans>Verify your email</Trans></p>
                <p>
                  <Trans>
                    If <strong>{email}</strong> can receive mail, we've sent it a 6-digit code. Enter it below —
                    your account is only created after this step. The code expires in 10 minutes.
                  </Trans>
                </p>
              </div>
              {codeError && (
                <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg text-red-700 dark:text-red-400 text-sm">
                  {codeError}
                </div>
              )}
              <form onSubmit={handleVerifyAndRegister} className="space-y-3">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={signupCode}
                  onChange={(e) => { setSignupCode(e.target.value.replace(/\D/g, '')); setCodeError(''); }}
                  className="w-full px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-[0.5em] font-mono"
                  style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={verifyBusy || signupCode.length !== 6}
                  className="w-full py-2 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {verifyBusy ? t`Verifying…` : t`Verify & create account`}
                </button>
              </form>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendBusy || resendCooldown > 0}
                className="w-full py-2 border border-slate-200 dark:border-slate-600 rounded-lg font-medium hover:bg-white dark:hover:bg-slate-700 transition-all disabled:opacity-50"
              >
                {resendBusy ? t`Sending…` : resendCooldown > 0 ? t`Resend code (${resendCooldown}s)` : t`Resend code`}
              </button>
              {resendMessage && (
                <p className="text-sm text-center text-slate-600 dark:text-slate-400">{resendMessage}</p>
              )}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setCodeStep(false); setCodeError(''); setResendMessage(''); }}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Trans>← Use a different email</Trans>
                </button>
              </div>
            </div>
          ) : (
          <>
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg text-red-700 dark:text-red-400 text-sm">
              <p>{error}</p>
              {needsVerification && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => handleResend(needsVerification)}
                    disabled={resendBusy}
                    className="text-sm font-semibold underline hover:no-underline disabled:opacity-50"
                  >
                    {resendBusy ? t`Sending…` : t`Resend verification email to ${needsVerification}`}
                  </button>
                  {resendMessage && (
                    <p className="mt-2 text-sm text-red-700/80 dark:text-red-400/80">{resendMessage}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wider"><Trans>First Name</Trans></label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wider"><Trans>Last Name</Trans></label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wider"><Trans>Email</Trans></label>
              <input
                type="email"
                className="w-full px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wider"><Trans>Password</Trans></label>
              <input
                type="password"
                className="w-full px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={isLogin ? undefined : 8}
              />
              {!isLogin && (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{PASSWORD_HINT}</p>
              )}
              {isLogin && (
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password', { state: { email } })}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <Trans>Forgot password?</Trans>
                  </button>
                </div>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-2 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
              disabled={loading}
            >
              {loading ? t`Please wait...` : (isLogin ? t`Sign In` : t`Create Account`)}
            </button>
          </form>

          <div className="mt-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
              <span className="text-xs text-slate-400 uppercase"><Trans>OR</Trans></span>
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                className="w-full py-2 border border-slate-200 dark:border-slate-600 rounded-lg font-medium text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                onClick={() => handleSSO('google')}
                disabled={loading}
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.96-2.184l-2.908-2.258c-.806.54-1.837.86-3.052.86-2.347 0-4.337-1.584-5.047-3.715H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.953 10.703c-.18-.54-.282-1.117-.282-1.703s.102-1.163.282-1.703V4.965H.957C.348 6.175 0 7.55 0 9s.348 2.825.957 4.035l3.996-3.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.965L3.953 7.297C4.663 5.163 6.653 3.58 9 3.58z"/>
                </svg>
                <Trans>Continue with Google</Trans>
              </button>
              <button
                type="button"
                className="w-full py-2 border border-slate-200 dark:border-slate-600 rounded-lg font-medium text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                onClick={() => handleSSO('microsoft')}
                disabled={loading}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path fill="#F25022" d="M0 0h8.5v8.5H0z"/>
                  <path fill="#00A4EF" d="M9.5 0H18v8.5H9.5z"/>
                  <path fill="#7FBA00" d="M0 9.5h8.5V18H0z"/>
                  <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z"/>
                </svg>
                <Trans>Continue with Microsoft</Trans>
              </button>
            </div>
          </div>

          <div className="text-center mt-6">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setNeedsVerification(null);
                setResendMessage('');
              }}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              {isLogin ? (
                <Trans>Don't have an account? Sign up</Trans>
              ) : (
                <Trans>Already have an account? Sign in</Trans>
              )}
            </button>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
